import React from 'react';

import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { tryParseCustomComponentChild } from './customComponentParsers';
import { OperationCollectionEntryMarkdownElement } from './OperationCollectionEntryMarkdownElement';
import { OperationCollectionMarkdownElement } from './OperationCollectionMarkdownElement';

// We can't use markdown-to-jsx's built-in custom React component method
// (https://www.npmjs.com/package/markdown-to-jsx#optionsoverrides---rendering-arbitrary-react-components)
// because it requires `disableParsingRawHTML` to be false. To write custom components,
// we embed the data we need in the body of paragraph tags & parse it out
// when we render our custom versions of standard paragraph tags.

// If you want to add a custom component:
//   1. make a regex & a parser for the text you are going to turn into a custom compoenent
//   2. add a branch in the returns below to return your custom component
export const CustomComponentsOrParagraph = ({
  children,
  collectionEntryData,
  collectionEntryDataLoading,
  collectionsData,
  collectionsDataLoading,
  className,
  ...props
}: {
  children: React.ReactNode;
  collectionEntryData:
    | GraphQLTypes.OperationCollectionEntriesReadmeQuery
    | undefined;
  collectionEntryDataLoading: boolean;
  collectionsData:
    | GraphQLTypes.OperationCollectionCollectionsReadmeQuery
    | undefined;
  collectionsDataLoading: boolean;
  className?: string;
}) => {
  const contentOfSingleChild =
    Array.isArray(children) && children.length === 1 ? children[0] : undefined;
  const parsedChild = tryParseCustomComponentChild(
    typeof contentOfSingleChild === 'string' ? contentOfSingleChild : '',
  );
  const type = parsedChild.type;
  switch (type) {
    case 'saved':
      return (
        <OperationCollectionEntryMarkdownElement
          operation={collectionEntryData?.operationCollectionEntries.find(
            (entry) => entry.id === parsedChild.id,
          )}
          loading={collectionEntryDataLoading}
        />
      );
    case 'collection':
      return (
        <OperationCollectionMarkdownElement
          collection={collectionsData?.service?.variant?.operationCollections.find(
            (coll) => coll.id === parsedChild.id,
          )}
          loading={collectionsDataLoading}
        />
      );
    case 'none':
      return (
        <p {...props} className={className}>
          {children}
        </p>
      );
    default:
      assertUnreachable(type);
  }
};
